<script lang="ts">
	import type { Snippet } from 'svelte';

	interface Props {
		/** Small primary-colored label above the title. Use sparingly (see eyebrow discipline). */
		eyebrow?: string;
		title: string;
		/** Optional substring of `title` rendered in the brand accent color (echoes the hero). */
		accent?: string;
		align?: 'center' | 'left';
		/** Override the default title type scale (e.g. smaller titles in narrow columns). */
		titleClass?: string;
		class?: string;
		/** Optional supporting paragraph below the title. */
		children?: Snippet;
	}

	let {
		eyebrow,
		title,
		accent,
		align = 'center',
		titleClass = 'text-4xl font-extrabold tracking-tight text-balance text-surface-950-50 sm:text-5xl md:text-6xl/[1.05]',
		class: className = '',
		children
	}: Props = $props();

	// Split the title around the accent substring so one word can carry the brand
	// magenta without fragmenting the heading for screen readers.
	let parts = $derived.by(() => {
		if (!accent) return null;
		const i = title.indexOf(accent);
		if (i === -1) return null;
		return { before: title.slice(0, i), match: accent, after: title.slice(i + accent.length) };
	});
</script>

<div
	class="flex flex-col {align === 'center'
		? 'items-center text-center'
		: 'items-start text-left'} {className}"
>
	{#if eyebrow}
		<div class="mb-5 text-md font-semibold text-primary-500">{eyebrow}</div>
	{/if}
	<h2 class="max-w-3xl {titleClass}">
		{#if parts}{parts.before}<span class="text-tertiary-700 dark:text-tertiary-400"
				>{parts.match}</span
			>{parts.after}{:else}{title}{/if}
	</h2>
	{#if children}
		<div
			class="mt-5 max-w-2xl text-sm text-pretty text-secondary-700 sm:text-base lg:text-md dark:text-secondary-200"
		>
			{@render children?.()}
		</div>
	{/if}
</div>
